import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '@/api/api';
import { ENDPOINT } from '@/constants/endpoints';
import { UpdateUserRolePayload, User, UsersParams, UsersResponse } from '@/types/users';
import { UpdateMePayload, ViewerMeResponse } from '@/types/viewerMe';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: axiosBaseQuery,
  tagTypes: ['Users', 'ViewerMe'],
  endpoints: builder => ({
    getMe: builder.query<ViewerMeResponse, void>({
      query: () => ({
        url: ENDPOINT.ACCOUNT_SETTINGS.GET_ME(),
        method: 'GET',
      }),
      providesTags: ['ViewerMe'],
    }),
    dismissBanner: builder.mutation<void, string>({
      query: bannerId => ({
        url: ENDPOINT.ACCOUNT_SETTINGS.POST_DISMISSED_BANNERS(),
        method: 'POST',
        body: { bannerId },
      }),
      invalidatesTags: ['ViewerMe'],
    }),
    updateMe: builder.mutation<ViewerMeResponse, UpdateMePayload>({
      query: payload => ({
        url: ENDPOINT.ACCOUNT_SETTINGS.GET_ME(),
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['ViewerMe'],
    }),
    getUsers: builder.query<UsersResponse, UsersParams>({
      query: params => ({
        url: ENDPOINT.USERS.GET_ALL(),
        method: 'GET',
        params,
      }),
      providesTags: ['Users'],
    }),
    getUsersPaginated: builder.infiniteQuery<UsersResponse, UsersParams, string | undefined>({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: lastPage =>
          lastPage.hasMore && lastPage.cursor ? lastPage.cursor : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: ENDPOINT.USERS.GET_ALL(),
        method: 'GET',
        params: {
          ...queryArg,
          cursor: pageParam,
        },
      }),
      providesTags: ['Users'],
    }),
    updateUserRole: builder.mutation<User, UpdateUserRolePayload>({
      query: ({ userId, role }) => ({
        url: ENDPOINT.USERS.UPDATE_ROLE(userId),
        method: 'PATCH',
        body: {
          role,
        },
      }),
      invalidatesTags: ['Users'],
    }),
  }),
});

export const {
  useGetMeQuery,
  useDismissBannerMutation,
  useUpdateMeMutation,
  useGetUsersPaginatedInfiniteQuery,
  useUpdateUserRoleMutation,
} = usersApi;
